/**
 * Admin API client — talks to the same-origin `/api/admin/*` endpoints for player
 * data management (support / bug-fixing). Every call carries the authenticated
 * headers from the save store (Clerk Bearer token); the SERVER gates access to
 * allowlisted admins, so these helpers simply surface its responses.
 *
 * This is intentionally thin: the admin UI owns the editing experience; here we
 * just do the fetches + typed shapes.
 */

import { authHeaders } from "./save";
import { clearSandboxLocal, setSandbox, setSandboxStage } from "./sandbox";
import type { SaveData } from "./save";
import { coerceKickstarter, KICKSTARTER_OFF, type KickstarterConfig } from "./kickstarter";
import { coercePrelaunch, PRELAUNCH_OFF, type PrelaunchConfig } from "./prelaunch";
import type { RedeemClaim } from "./redeem";
import type { ReleaseState } from "./releases";

/** A resolved player identity (Clerk profile), when the player signed in. */
export interface AdminIdentity {
  email: string | null;
  name: string | null;
  imageUrl?: string | null;
}

/** One row in the admin player list (a compact summary). */
export interface AdminPlayerRow {
  playerId: string;
  updatedAt: string;
  online?: boolean;        // is the player's game connected right now (live presence)
  email: string | null;
  name: string | null;
  /** their in-game handle ("Rosewood#4821"), when they have set a name */
  handle?: string | null;
  coins: number;
  xp: number;
  rackCount: number;
  plantings: number;
  // usage rollup (active-play ms + last-seen); present once the player has any recorded usage.
  playMs?: number;
  greenhouseMs?: number;
  gardenMs?: number;
  fishingMs?: number;
  sessions?: number;
  lastSeenMs?: number;
}

/** Aggregate usage statistics across all players (for the admin Stats panel). */
export interface AdminStats {
  players: number;         // total saved players
  active: number;          // players with any recorded usage (averages are over these)
  totals: { greenhouseMs: number; gardenMs: number; fishingMs: number; sessions: number; playMs: number };
  averages: { greenhouseMs: number; gardenMs: number; fishingMs: number; sessions: number; playMs: number };
  split: { greenhousePct: number; gardenPct: number };
  lastSeenMs: number;      // most recent heartbeat across all players
  leaderboard: Array<{
    playerId: string; email: string | null; name: string | null; online?: boolean;
    playMs: number; greenhouseMs: number; gardenMs: number; fishingMs: number; sessions: number; lastSeenMs: number;
  }>;
}

export interface AdminPlayerDetail {
  playerId: string;
  updatedAt: string;
  data: SaveData;
  identity?: AdminIdentity | null;
}

/** Query options for the paginated/searchable player list. */
export interface AdminListOpts {
  q?: string;
  sort?: "updated" | "coins" | "xp" | "racks";
  dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

/** One admin audit-log entry. */
export interface AdminAuditEntry {
  id: number;
  at: string;
  admin_id: string;
  admin_email: string | null;
  action: string;
  target_id: string | null;
  detail: unknown;
}

/** A quick support action + its parameters. */
export type AdminActionName =
  | "grantCoins" | "setCoins" | "grantXp" | "setXp"
  | "addRacks" | "setRacks" | "giveSeed" | "giveStock"
  | "clearSeeds" | "clearStock" | "resetGrowth" | "clearPlantings"
  /** Garden Club comps: a free membership (optionally `{ days }`), and taking one away. Paid ones are Stripe's. */
  | "grantClub" | "revokeClub";

async function asJson(res: Response): Promise<unknown> {
  const txt = await res.text();
  let body: unknown = undefined;
  try { body = txt ? JSON.parse(txt) : undefined; } catch { /* non-JSON error body */ }
  if (!res.ok) {
    const msg = (body && typeof body === "object" && "error" in body) ? String((body as { error: unknown }).error) : `request failed (${res.status})`;
    throw new Error(msg);
  }
  return body;
}

/** Whoami: is the current session an admin? Also returns which environment this server is (production
 *  or staging) + the peer server URLs (stagingUrl set on live, liveUrl set on test) for cross-server
 *  navigation (the admin Play/Test chooser + the admin env panel). */
export interface AdminWhoami {
  admin: boolean; authEnabled: boolean; env: "production" | "staging"; stagingUrl: string | null; liveUrl: string | null;
  maintenance: boolean; maintenanceAllowed: boolean; kickstarter: KickstarterConfig;
  /** pre-launch mode — for the admin console's status strip (the server has always sent it) */
  prelaunch: PrelaunchConfig;
}
export async function adminWhoami(): Promise<AdminWhoami> {
  try {
    const res = await fetch("/api/admin/me", { headers: await authHeaders() });
    const body = (await asJson(res)) as { admin?: boolean; authEnabled?: boolean; env?: string; stagingUrl?: string | null; liveUrl?: string | null; maintenance?: boolean; maintenanceAllowed?: boolean; kickstarter?: unknown; prelaunch?: unknown };
    return {
      admin: !!body?.admin, authEnabled: !!body?.authEnabled,
      env: body?.env === "staging" ? "staging" : "production",
      stagingUrl: body?.stagingUrl ?? null,
      liveUrl: body?.liveUrl ?? null,
      maintenance: !!body?.maintenance,
      maintenanceAllowed: !!body?.maintenanceAllowed,
      kickstarter: coerceKickstarter(body?.kickstarter),
      prelaunch: coercePrelaunch(body?.prelaunch),
    };
  } catch {
    return { admin: false, authEnabled: false, env: "production", stagingUrl: null, liveUrl: null, maintenance: false, maintenanceAllowed: false, kickstarter: { ...KICKSTARTER_OFF }, prelaunch: { ...PRELAUNCH_OFF } };
  }
}

/** Toggle MAINTENANCE MODE for this server (admin only). Returns the new state. */
export async function adminSetMaintenance(on: boolean): Promise<{ ok: boolean; maintenance: boolean }> {
  const res = await fetch("/api/admin/maintenance", {
    method: "POST",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ on }),
  });
  return (await asJson(res)) as { ok: boolean; maintenance: boolean };
}

/** Broadcast a LIVE ANNOUNCEMENT — pops up centre-screen on every connected player's game, then fades.
 *  Returns how many players it reached. */
export async function adminAnnounce(text: string): Promise<{ ok: boolean; reached: number }> {
  const res = await fetch("/api/admin/announce", {
    method: "POST",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ text }),
  });
  return (await asJson(res)) as { ok: boolean; reached: number };
}

/** FORCE-REFRESH every connected player's game so it reloads onto the latest deployed build.
 *  Returns how many players were told to reload. */
export async function adminForceReload(): Promise<{ ok: boolean; reached: number }> {
  const res = await fetch("/api/admin/force-reload", { method: "POST", headers: await authHeaders() });
  return (await asJson(res)) as { ok: boolean; reached: number };
}

/** A decor collision/occlusion band (mirrors sim/decor.ts DecorBand). */
export type AdminDecorBand = { top: number; bottom: number; profile: number[] };

/** Get the LIVE decor-band overrides currently stored on the server (for the in-app collision editor). */
export async function adminGetDecorBands(): Promise<Record<string, AdminDecorBand>> {
  const res = await fetch("/api/admin/decor-bands", { headers: await authHeaders() });
  const body = (await asJson(res)) as { bands?: Record<string, AdminDecorBand> };
  return body?.bands ?? {};
}
/** Replace the LIVE decor-band overrides (goes live immediately — open games pick it up on their next poll). */
export async function adminSaveDecorBands(bands: Record<string, AdminDecorBand>): Promise<{ ok: boolean; count: number }> {
  const res = await fetch("/api/admin/decor-bands", {
    method: "POST",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ bands }),
  });
  return (await asJson(res)) as { ok: boolean; count: number };
}

/** Deep-merge a PARTIAL patch into the live TUNING blob (admin only). Goes live immediately — open games
 *  pick it up on their next /api/tuning poll. A `null` value inside the patch deletes that key. */
export async function adminSaveTuning(patch: Record<string, unknown>): Promise<{ ok: boolean; keys: string[]; bytes: number }> {
  const res = await fetch("/api/admin/tuning", {
    method: "POST",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ patch }),
  });
  return (await asJson(res)) as { ok: boolean; keys: string[]; bytes: number };
}

/** REPLACE whole top-level tuning keys (admin only): each key is stored exactly as given, a `null` value deletes
 *  it. What an editor that owns a key should send — `adminSaveTuning`'s deep merge keeps fields the editor
 *  removed and reads a meaningful `null` inside the value as a delete (server/index.js applyTuningWrite). */
export async function adminSetTuningKeys(set: Record<string, unknown>): Promise<{ ok: boolean; keys: string[]; bytes: number }> {
  const res = await fetch("/api/admin/tuning", {
    method: "POST",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ set }),
  });
  return (await asJson(res)) as { ok: boolean; keys: string[]; bytes: number };
}


/** SEED (or remove) the demo gardener profiles — STAGING ONLY, admin. Used to populate the directory with
 *  hand-authored gardens so the visiting flow can be tested against real data. */
export async function adminSeedProfiles(remove = false): Promise<{ ok: boolean; seeded: number; removed: number }> {
  const res = await fetch("/api/admin/seed-profiles", {
    method: "POST",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ remove }),
  });
  return (await asJson(res)) as { ok: boolean; seeded: number; removed: number };
}

/** PROMOTE a theme tuning key from TEST → LIVE (staging-only; admin). `key` selects the site theme (default)
 *  or the in-game theme. Returns the live URL it pushed to. */
export async function adminPromoteTheme(key: "siteTheme" | "gameTheme" = "siteTheme"): Promise<{ ok: boolean; liveUrl: string; key: string }> {
  const res = await fetch("/api/admin/promote-theme", {
    method: "POST",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ key }),
  });
  return (await asJson(res)) as { ok: boolean; liveUrl: string; key: string };
}

/** Get the MAINTENANCE ALLOWLIST — emails allowed to keep playing during maintenance (admin only). */
export async function adminGetMaintenanceAllowlist(): Promise<string[]> {
  const res = await fetch("/api/admin/maintenance-allowlist", { headers: await authHeaders() });
  const body = (await asJson(res)) as { emails?: string[] };
  return Array.isArray(body?.emails) ? body.emails : [];
}
/** Replace the maintenance allowlist (admin only). Returns the cleaned, stored list. */
export async function adminSetMaintenanceAllowlist(emails: string[]): Promise<string[]> {
  const res = await fetch("/api/admin/maintenance-allowlist", {
    method: "PUT",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ emails }),
  });
  const body = (await asJson(res)) as { emails?: string[] };
  return Array.isArray(body?.emails) ? body.emails : [];
}

/** Sync MY OWN save from the LIVE server into THIS (test) server's DB. Only works when this deployment
 *  is the staging/test server; on production the endpoint refuses. Returns a summary of what imported. */
export async function adminSyncMySave(): Promise<{ ok: boolean; imported?: unknown }> {
  const res = await fetch("/api/admin/sync-my-save", { method: "POST", headers: await authHeaders() });
  return (await asJson(res)) as { ok: boolean; imported?: unknown };
}

/** List player saves with search/sort/pagination. Admin only. Returns rows + total. */
export async function adminListPlayers(opts: AdminListOpts = {}): Promise<{ players: AdminPlayerRow[]; total: number }> {
  const p = new URLSearchParams();
  if (opts.q) p.set("q", opts.q);
  if (opts.sort) p.set("sort", opts.sort);
  if (opts.dir) p.set("dir", opts.dir);
  p.set("limit", String(opts.limit ?? 50));
  p.set("offset", String(opts.offset ?? 0));
  const res = await fetch(`/api/admin/players?${p.toString()}`, { headers: await authHeaders() });
  const body = (await asJson(res)) as { players?: AdminPlayerRow[]; total?: number };
  return { players: body?.players ?? [], total: body?.total ?? 0 };
}

/** Apply a quick support action to a player; returns the updated detail. Admin only. */
export async function adminPlayerAction(playerId: string, action: AdminActionName, params: Record<string, unknown> = {}): Promise<AdminPlayerDetail> {
  const res = await fetch(`/api/admin/players/${encodeURIComponent(playerId)}/action`, {
    method: "POST",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ action, params }),
  });
  return (await asJson(res)) as AdminPlayerDetail;
}

/** Fetch the recent admin audit log (optionally for one target player). Admin only. */
export async function adminAudit(opts: { limit?: number; targetId?: string } = {}): Promise<AdminAuditEntry[]> {
  const p = new URLSearchParams();
  p.set("limit", String(opts.limit ?? 100));
  if (opts.targetId) p.set("targetId", opts.targetId);
  const res = await fetch(`/api/admin/audit?${p.toString()}`, { headers: await authHeaders() });
  const body = (await asJson(res)) as { entries?: AdminAuditEntry[] };
  return body?.entries ?? [];
}

/** Fetch aggregate usage statistics across all players (per-metric totals + averages + leaderboard). Admin only. */
export async function adminStats(): Promise<AdminStats> {
  const res = await fetch(`/api/admin/stats`, { headers: await authHeaders() });
  return (await asJson(res)) as AdminStats;
}

/** Fetch one player's full save. Admin only. */
export async function adminGetPlayer(playerId: string): Promise<AdminPlayerDetail> {
  const res = await fetch(`/api/admin/players/${encodeURIComponent(playerId)}`, { headers: await authHeaders() });
  return (await asJson(res)) as AdminPlayerDetail;
}

/** Overwrite a player's save with `data`. Admin only. Returns the server-canonical save. */
export async function adminSavePlayer(playerId: string, data: SaveData): Promise<AdminPlayerDetail> {
  const res = await fetch(`/api/admin/players/${encodeURIComponent(playerId)}`, {
    method: "PUT",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(data),
  });
  return (await asJson(res)) as AdminPlayerDetail;
}

/** Delete (reset) a player's save. Admin only. */
export async function adminDeletePlayer(playerId: string): Promise<number> {
  const res = await fetch(`/api/admin/players/${encodeURIComponent(playerId)}`, {
    method: "DELETE",
    headers: await authHeaders(),
  });
  const body = (await asJson(res)) as { deleted?: number };
  return body?.deleted ?? 0;
}

// ---- Kickstarter Mode + the overview dashboard --------------------------------------------------------------

/** The stored Kickstarter Mode config (sim/kickstarter.ts). */
export async function adminGetKickstarter(): Promise<KickstarterConfig> {
  const res = await fetch("/api/admin/kickstarter", { headers: await authHeaders() });
  return coerceKickstarter(await asJson(res));
}

/** Save Kickstarter Mode. Returns what the server stored (clamped/cleaned). Audited server-side. */
export async function adminSetKickstarter(cfg: KickstarterConfig): Promise<KickstarterConfig> {
  const res = await fetch("/api/admin/kickstarter", {
    method: "PUT", headers: await authHeaders({ "content-type": "application/json" }), body: JSON.stringify(cfg),
  });
  return coerceKickstarter(await asJson(res));
}

// ---- Content releases (sim/releases.ts, server/releases.js) ----------------------------------------------------

/** One declared release as the admin Releases page shows it: its stored state, what it shows right now, and the
 *  content tagged with it (ids per kind). */
export interface AdminRelease {
  id: string; title: string; state: ReleaseState | null; view: "off" | "preview" | "on";
  content: { plants: string[]; decor: string[]; fish: string[]; insects: string[] };
  /** patch notes on this server that wait for (or came out with) this release */
  notes?: number;
}
export async function adminGetReleases(): Promise<{ env: "production" | "staging"; now: number; releases: AdminRelease[] }> {
  const res = await fetch("/api/admin/releases", { headers: await authHeaders() });
  return (await asJson(res)) as { env: "production" | "staging"; now: number; releases: AdminRelease[] };
}
/** Save every release's state at once (the server keeps only releases the game declares). Audited server-side. */
export async function adminSetReleases(states: Record<string, ReleaseState>): Promise<Record<string, ReleaseState>> {
  const res = await fetch("/api/admin/releases", {
    method: "PUT", headers: await authHeaders({ "content-type": "application/json" }), body: JSON.stringify({ states }),
  });
  return ((await asJson(res)) as { states: Record<string, ReleaseState> }).states;
}

/** The stored pre-launch mode config (sim/prelaunch.ts). */
export async function adminGetPrelaunch(): Promise<PrelaunchConfig> {
  const res = await fetch("/api/admin/prelaunch", { headers: await authHeaders() });
  return coercePrelaunch(await asJson(res));
}

/** One early-access code as the admin list shows it — never the code itself (it is stored hashed), only its hint. */
export interface AdminAccessCode {
  id: number; hint: string; label: string; createdAt: string;
  usedBy: { playerId: string; name: string | null; email: string | null } | null;
  usedAt: string | null; revoked: boolean;
}
export async function adminListAccessCodes(): Promise<AdminAccessCode[]> {
  const res = await fetch("/api/admin/access-codes", { headers: await authHeaders() });
  return ((await asJson(res)) as { codes: AdminAccessCode[] }).codes;
}
/** Make codes for one person (or `count` numbered ones). The FULL codes come back here once and never again. */
export async function adminCreateAccessCodes(label: string, count: number): Promise<{ id: number; code: string; label: string }[]> {
  const res = await fetch("/api/admin/access-codes", {
    method: "POST", headers: await authHeaders({ "content-type": "application/json" }), body: JSON.stringify({ label, count }),
  });
  return ((await asJson(res)) as { codes: { id: number; code: string; label: string }[] }).codes;
}
/** Revoke a code: it stops working, and the account it was bound to (if any) loses early access. */
export async function adminRevokeAccessCode(id: number): Promise<void> {
  const res = await fetch(`/api/admin/access-codes/${id}/revoke`, { method: "POST", headers: await authHeaders({ "content-type": "application/json" }), body: "{}" });
  await asJson(res);
}

/** Save pre-launch mode. Returns what the server stored (cleaned). Audited server-side. */
export async function adminSetPrelaunch(cfg: PrelaunchConfig): Promise<PrelaunchConfig> {
  const res = await fetch("/api/admin/prelaunch", {
    method: "PUT", headers: await authHeaders({ "content-type": "application/json" }), body: JSON.stringify(cfg),
  });
  return coercePrelaunch(await asJson(res));
}

/** The admin console's landing dashboard. */
export interface AdminOverview {
  players: number; online: number;
  newToday: number; newThisWeek: number; activeToday: number; activeThisWeek: number;
  members: number; paidMembers: number;
  /** players with ANY track at the Kickstarter cap, and the count per track */
  atLevelCap: number; atLevelCapByTrack?: { garden: number; fish: number; bug: number };
  maintenance: boolean; kickstarter: KickstarterConfig; paymentsEnabled: boolean; env: "production" | "staging";
  newest: Array<{ playerId: string; firstSeenMs: number; name: string | null; email: string | null; online: boolean }>;
  recent: AdminAuditEntry[];
}

export async function adminOverview(): Promise<AdminOverview> {
  const res = await fetch("/api/admin/overview", { headers: await authHeaders() });
  const o = (await asJson(res)) as AdminOverview;
  return { ...o, kickstarter: coerceKickstarter(o.kickstarter), newest: o.newest ?? [], recent: o.recent ?? [] };
}

// ---- the PLAYER ACCOUNT page (server/adminAccount.js; src/ui/site/AdminAccount.tsx) ---------------------------

/** A skill track, as the account page names it. */
export type AccountTrack = "garden" | "fish" | "bug";

/** Every change the account page can make. Grants ride the redemption ledger; the rest reload a running game. */
export type AccountActionName =
  | "addCoins" | "removeCoins" | "addPetals" | "removePetals"
  | "addSeeds" | "removeSeeds" | "addDecor" | "removeDecor"
  | "grantAchievement" | "revokeAchievement" | "grantPlant" | "revokePlant"
  | "setLevel" | "grantClub" | "revokeClub"
  /** a reward pack (sim/rewardPacks.ts): several Kickstarter rewards at once, minus what they already own */
  | "grantPack" | "revokePack"
  /** take back one directly granted backer cosmetic (sim/backerCosmetics.ts) */
  | "revokeCosmetic";

/** One before → after line of a preview (or of a logged change). */
export interface AccountChange { key: string; label: string; from: string | number; to: string | number }

/** What an account change recorded in the audit log. */
export interface AccountAuditDetail {
  action?: string;
  params?: Record<string, unknown>;
  note?: string;
  changes?: AccountChange[];
  undo?: { action: string; params: Record<string, unknown> } | null;
  undoOf?: string;
}

export interface AdminAccount {
  playerId: string;
  updatedAt: string;
  online: boolean;
  identity: AdminIdentity | null;
  handle: string | null;
  nameStyle: string;
  /** their public profile and the admin HIDE switch (migration 011); null when they never opened the profile */
  profile: { publicProfile: boolean; hidden: boolean } | null;
  /** false for a heartbeat-only row: they have never saved a garden, so nothing can be changed yet */
  hasGarden: boolean;
  account: {
    coins: number;
    petals: number;
    tracks: Record<AccountTrack, { xp: number; level: number }>;
    seeds: Record<string, number>;
    decorBag: Record<string, number>;
    decorPlaced: Record<string, number>;
    achievements: string[];
    grantedAchievements: string[];
    /** backer skins / name styles granted on their own ("pot:voidAnodized") — the ledger is their only record */
    grantedCosmetics: string[];
    nameStyles: string[];
  };
  club: { status: string; periodEnd: number | null; paid: boolean };
  entitlements: { productId: number | null; name: string | null; plantId: string | null; paid: boolean; grantedAt: string }[];
  purchases: { id: number; product: string | null; amountCents: number; currency: string; status: string; createdAt: string; paidAt: string | null }[];
  redemptions: RedeemClaim[];
  history: { id: number; at: string; adminId: string; adminEmail: string | null; action: string; detail: AccountAuditDetail | null; undoable: boolean; undone: boolean }[];
}

/** A change's (or a preview's) result. `live` says what the player's running game did with it. */
export interface AccountChangeResult {
  ok: true;
  changes: AccountChange[];
  undoable: boolean;
  dryRun?: boolean;
  /** preview only: is their game open right now */
  online?: boolean;
  /** preview only: what to know before confirming, e.g. a level above the Kickstarter cap (server/adminAccount.js accountWarnings) */
  warnings?: string[];
  auditId?: number;
  live?: "applied" | "reloaded" | "offline";
}

/** One player's account at a glance. Admin only. */
export async function adminGetAccount(playerId: string): Promise<AdminAccount> {
  const res = await fetch(`/api/admin/account/${encodeURIComponent(playerId)}`, { headers: await authHeaders() });
  return (await asJson(res)) as AdminAccount;
}

/** Preview (`dryRun`) or make one account change. Admin only; audited when not a preview. */
export async function adminAccountAction(playerId: string, action: AccountActionName, params: Record<string, unknown>,
  opts: { dryRun?: boolean; note?: string } = {}): Promise<AccountChangeResult> {
  const res = await fetch(`/api/admin/account/${encodeURIComponent(playerId)}/action`, {
    method: "POST",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ action, params, dryRun: opts.dryRun === true, note: opts.note ?? "" }),
  });
  return (await asJson(res)) as AccountChangeResult;
}

/** HIDE (or unhide) a player's public profile: out of the directory and search, 404 to everyone but admins.
 *  Admin only; audited as profile:hide / profile:unhide. */
export async function adminSetProfileHidden(playerId: string, hidden: boolean, note = ""): Promise<{ publicProfile: boolean; hidden: boolean }> {
  const res = await fetch(`/api/admin/account/${encodeURIComponent(playerId)}/profile-hidden`, {
    method: "POST",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ hidden, note }),
  });
  return ((await asJson(res)) as { profile: { publicProfile: boolean; hidden: boolean } }).profile;
}

/** Preview (`dryRun`) or make the undo of one logged account change. Admin only. */
export async function adminAccountUndo(playerId: string, auditId: number, dryRun = false): Promise<AccountChangeResult> {
  const res = await fetch(`/api/admin/account/${encodeURIComponent(playerId)}/undo`, {
    method: "POST",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ auditId, dryRun }),
  });
  return (await asJson(res)) as AccountChangeResult;
}

/**
 * THE TUTORIAL TEST SAVE (sim/sandbox.ts). Start (or restart): drop this admin's throwaway row on the server, forget
 * its local copies, and switch THIS TAB to it — the next load of /play is a brand-new game. The caller reloads.
 */
export async function adminStartSandbox(stage?: string): Promise<void> {
  const res = await fetch("/api/admin/sandbox/reset", { method: "POST", headers: await authHeaders({ "content-type": "application/json" }), body: "{}" });
  // (a local dev build with no backend, or none with auth, has no server row to clear — its save is the local copy)
  if (!(import.meta.env.DEV && !res.ok)) await asJson(res);
  clearSandboxLocal();
  setSandboxStage(stage ?? null);   // a tutorial step to open on (sim/tutorialStages.ts) — none = the very start
  setSandbox(true);
}
/** Quit: drop the throwaway row and its local copies, and switch this tab back to the real game. The caller reloads. */
export async function adminEndSandbox(): Promise<void> {
  try {
    const res = await fetch("/api/admin/sandbox/reset", { method: "POST", headers: await authHeaders({ "content-type": "application/json" }), body: "{}" });
    await asJson(res);
  } finally {
    clearSandboxLocal();
    setSandbox(false);   // whatever the server said, this tab goes back to the real save
  }
}

/** THE TUTORIAL FUNNEL (server/guideFunnel.js): where new players drop out of a guide chapter. */
export interface GuideFunnel {
  chapter: string; starters: number; finished: number; skipped: number; inProgress: number; idleHours: number;
  steps: Array<{ id: string; reached: number; here: number; stuck: number; skippedHere: number }>;
}
export async function adminGuideFunnel(chapter: string, steps: string[], after?: string): Promise<GuideFunnel> {
  const q = new URLSearchParams({ chapter, steps: steps.join(","), ...(after ? { after } : {}) });
  const res = await fetch(`/api/admin/guide-funnel?${q}`, { headers: await authHeaders() });
  return (await asJson(res)) as GuideFunnel;
}

/**
 * THE ANALYTICS DASHBOARDS' DATA (server/analyticsAdmin.js): per-UTC-day series for `from`..`to` and the period
 * before it. `days` starts at the previous period (or 14 days back, whichever is earlier); every series is aligned to
 * it. The chosen range is the last `n` entries, the comparison the `n` before.
 */
export interface AnalyticsSeries {
  from: string; to: string; prevFrom: string; prevTo: string;
  days: string[];
  series: Record<string, number[]>;
}
/** traffic: analytics_daily · revenue: revenue_events · economy: economy_events · health: server_metrics_minute */
export type AnalyticsSource = "traffic" | "revenue" | "economy" | "health";

export async function adminAnalytics(source: AnalyticsSource, from: string, to: string, traffic?: "public" | "all"): Promise<AnalyticsSeries> {
  const q = new URLSearchParams({ from, to, ...(traffic ? { traffic } : {}) });
  const res = await fetch(`/api/admin/analytics/${source}?${q}`, { headers: await authHeaders() });
  return (await asJson(res)) as AnalyticsSeries;
}

/**
 * THE LIVE PAGE (server/liveAnalytics.js): who is on the site and in the game right now. Memory-only on the server,
 * so it is cheap to poll (the page does, every 5 s while visible). `counts.website` / `counts.game` are public
 * traffic; admins and tutorial test saves (`sandbox`) are counted apart.
 */
export type LiveTraffic = "public" | "admin" | "sandbox";
export interface LiveSession {
  sid: string | null; playerId: string | null; name: string | null; traffic: LiveTraffic; inGame: boolean;
  page: string | null; scene: string | null; country: string | null; region: string | null; city: string | null;
  device: string | null; referrer: string | null; since: number; lastSeen: number;
}
/** A feed entry: `name` is the EVENT (page_view, checkout_start, purchase, club_join…); `who` the player's name. */
export interface LiveEvent {
  t: number; name: string; page: string | null; props: Record<string, unknown> | null; sid: string | null;
  playerId: string | null; who: string | null; traffic: LiveTraffic; city: string | null; region: string | null; country: string | null;
}
export interface LiveSnapshot {
  now: number; windowMs: number; relay: boolean; total: number;
  counts: { website: number; game: number; admin: number; sandbox: number };
  sessions: LiveSession[];
  feed: LiveEvent[];
  pages: Array<{ k: string; n: number }>; scenes: Array<{ k: string; n: number }>;
}
export async function adminLive(): Promise<LiveSnapshot> {
  const res = await fetch("/api/admin/analytics/live", { headers: await authHeaders() });
  return (await asJson(res)) as LiveSnapshot;
}

/**
 * THE VISITORS PAGE (server/analyticsVisitors.js): the sessions of a date range — filtered, sorted and KEYSET-paginated
 * (pass the previous page's `next` as `after`) — one visitor's timeline, and the GEOGRAPHY / ACQUISITION / DEVICES
 * summaries. All read `analytics_sessions`, public traffic.
 */
export interface VisitorSession {
  sessionId: string; visitorId: string; playerId: string | null; name?: string | null; traffic: LiveTraffic;
  startedAt: string; lastSeenAt: string; events: number; pages: number; activeMs: number;
  country: string | null; region: string | null; city: string | null;
  device: string | null; browser: string | null; os: string | null; screen: string | null; touch: boolean | null;
  referrer: string | null; utmSource: string | null; utmMedium: string | null; utmCampaign: string | null; landing: string | null;
  played: boolean; signedUp: boolean; purchased: boolean;
}
export type SessionSort = "started" | "duration" | "pages" | "events";
/** The sessions table's filters, as the server reads them (`coerceFilters`); absent = any. */
export interface SessionFilters {
  country?: string; device?: string; signed?: "in" | "anon"; source?: string; landing?: string;
  played?: "yes" | "no"; purchased?: "yes" | "no";
}
export interface SessionsQuery extends SessionFilters { sort?: SessionSort; dir?: "asc" | "desc"; after?: string; limit?: number }
export interface SessionsPage { rows: VisitorSession[]; next: string | null; total: number | null; sort: SessionSort; dir: "asc" | "desc" }

function sessionsParams(from: string, to: string, q: SessionsQuery): URLSearchParams {
  const p = new URLSearchParams({ from, to });
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== "") p.set(k, String(v));
  return p;
}
export async function adminSessions(from: string, to: string, q: SessionsQuery = {}): Promise<SessionsPage> {
  const res = await fetch(`/api/admin/analytics/sessions?${sessionsParams(from, to, q)}`, { headers: await authHeaders() });
  return (await asJson(res)) as SessionsPage;
}
/** Every session the filter matches (not just the page on screen), as a CSV download. */
export async function adminSessionsCsv(from: string, to: string, q: SessionsQuery = {}): Promise<void> {
  const { after: _after, limit: _limit, ...rest } = q;
  const res = await fetch(`/api/admin/analytics/sessions.csv?${sessionsParams(from, to, rest)}`, { headers: await authHeaders() });
  if (!res.ok) await asJson(res);   // throws the server's error
  const a = document.createElement("a");
  a.href = URL.createObjectURL(await res.blob());
  a.download = `sessions-${from}-to-${to}.csv`;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/** A session on a visitor's timeline: the row, its page views in order, play time per scene and every event (`trail`). */
export interface TimelineSession extends VisitorSession {
  pageViews: string[];
  sceneMs: Partial<Record<"greenhouse" | "garden" | "other", number>>;
  trail: Array<{ t: string; name: string; page: string | null; props: Record<string, unknown> | null }>;
}
export interface VisitorTimeline {
  visitorId: string; players: string[]; visitors: string[]; names: Record<string, string | null>;
  sessions: TimelineSession[];
  money: Array<{ t: string; kind: string; amountCents: number | null; petals: number | null; product: string | null }>;
  truncated: boolean;
}
export async function adminVisitor(visitorId: string): Promise<VisitorTimeline> {
  const res = await fetch(`/api/admin/analytics/visitor/${encodeURIComponent(visitorId)}`, { headers: await authHeaders() });
  return (await asJson(res)) as VisitorTimeline;
}

export interface GeoRow { country: string | null; region: string | null; visitors: number; sessions: number; players: number; revenueCents: number }
export interface Geography { countries: Array<GeoRow & { regions: GeoRow[] }> }
export interface Conversion { visits: number; signups: number; players: number; payers: number }
export interface Acquisition {
  referrers: Array<Conversion & { referrer: string | null }>;
  campaigns: Array<Conversion & { source: string | null; medium: string | null; campaign: string | null }>;
  landings: Array<Conversion & { landing: string | null }>;
}
export interface DeviceRow { k: string | null; sessions: number; visitors: number; players: number }
export interface Devices { device: DeviceRow[]; browser: DeviceRow[]; os: DeviceRow[]; screen: DeviceRow[]; touch: DeviceRow[] }
export interface VisitorSummaries { geography: Geography; acquisition: Acquisition; devices: Devices }

/**
 * THE REVENUE PAGE (server/analyticsRevenue.js). Every money figure is in MINOR units of its own `currency` and keyed
 * by it — never summed across currencies. Refunds and disputes are negative and carry the product / plan of the
 * payment they reverse, so they net out there.
 */
export interface RevenueTotals {
  shopCents: number; clubCents: number; grossCents: number; refundCents: number; disputeCents: number; netCents: number;
  shopNetCents: number; clubNetCents: number; otherNetCents: number;
  payments: number; refunds: number; disputesOpened: number; disputesWon: number;
}
export interface RevenueProduct {
  currency: string; productId: number | null; kind: "product" | "club" | "unattributed"; name: string | null;
  sales: number; grossCents: number; refunds: number; refundCents: number; disputes: number; disputeCents: number; netCents: number;
}
export interface RevenueDispute {
  id: number; ts: string; amountCents: number; currency: string; state: "opened" | "won"; wonAt: string | null;
  playerId: string | null; name?: string | null; productId: number | null; product: string | null;
  stripeDispute: string | null; stripePayment: string | null;
}
export interface RevenueReport {
  from: string; to: string; days: string[]; currencies: string[];
  totals: Record<string, RevenueTotals>;
  byDay: Record<string, { shop: number[]; club: number[]; reversed: number[]; net: number[] }>;
  products: RevenueProduct[];
  disputes: RevenueDispute[];
}
export interface ClubMonth {
  month: string; partial: boolean; membersStart: number; membersEnd: number; newMembers: number; renewals: number;
  cancelsScheduled: number; cancelsUndone: number; ended: number; churnRate: number | null;
  money: Record<string, { newCents: number; renewalCents: number; churnedCents: number; reversedCents: number; mrrStartCents: number; mrrEndCents: number }>;
}
export interface ClubReport {
  from: string; to: string;
  now: { members: number; ending: number };
  months: ClubMonth[];
  length: { endedInRange: number; avgEndedDays: number | null; current: number; avgCurrentDays: number | null };
}
export interface MoneyFunnel {
  from: string; to: string; visitors: number; signups: number; players: number; payers: number; activePayers: number;
  sessionsFrom: string; partial: boolean;
}
export interface RevenuePage { "revenue-report": RevenueReport; club: ClubReport; "money-funnel": MoneyFunnel }

export async function adminRevenue<K extends keyof RevenuePage>(kind: K, from: string, to: string): Promise<RevenuePage[K]> {
  const res = await fetch(`/api/admin/analytics/${kind}?${new URLSearchParams({ from, to })}`, { headers: await authHeaders() });
  return (await asJson(res)) as RevenuePage[K];
}
/** Every revenue_events row of the range, as a CSV download (for the accountant). */
export async function adminRevenueCsv(from: string, to: string): Promise<void> {
  const res = await fetch(`/api/admin/analytics/revenue.csv?${new URLSearchParams({ from, to })}`, { headers: await authHeaders() });
  if (!res.ok) await asJson(res);   // throws the server's error
  const a = document.createElement("a");
  a.href = URL.createObjectURL(await res.blob());
  a.download = `revenue-${from}-to-${to}.csv`;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/**
 * THE ECONOMY PAGE (server/analyticsEconomy.js): Petals and coins. Day series are aligned to `days` (the range only).
 * Exchange groups: `member` / `non_member` as the exchange recorded it, `unknown` for rows from before it did.
 */
export type EarnSource = "checkin" | "achievement" | "redeem" | "grant" | "purchase" | "other";
export interface ExchangeGroup { exchanges: number; coins: number; petals: number; players: number }
export interface EconomyReport {
  from: string; to: string; days: string[];
  earned: Record<EarnSource, number[]>;
  spent: number[];
  exchange: { exchanges: number[]; coins: number[]; petals: number[] };
  bySource: Record<EarnSource, number>;
  groups: { member: ExchangeGroup; non_member: ExchangeGroup; unknown: ExchangeGroup };
  totals: { earned: number; spent: number; purchases: number; converted: number; coinsConverted: number; exchanges: number; converters: number };
  net: number;
  products: { productId: number | null; name: string | null; petals: number; sales: number; byDay: number[] }[];
}
export interface ShopFunnelProduct { productId: number; name: string | null; views: number; checkoutCash: number; checkoutPetals: number; paidCash: number; paidPetals: number }
export interface EconomyFunnels {
  from: string; to: string;
  shop: { opened: number; viewed: number; checkoutCash: number; checkoutPetals: number; paidCash: number; paidPetals: number; products: ShopFunnelProduct[] };
  club: { opened: number; joinClicked: number; checkouts: number; members: number };
  exchange: { opened: number; exchanges: number; converters: number };
}
export interface Holder { playerId: string; coins: number; petals: number; name?: string | null }
export interface Quantiles { p50: number; p90: number; p99: number; p999: number; max: number }
export interface EconomySnapshotRow {
  day: string; takenAt: string; saves: number; petalsTotal: number; coinsTotal: number;
  coinHolders: number; petalHolders: number; coins: Quantiles; petals: Quantiles;
  coinBuckets: { label: string; n: number; total: number }[]; petalBuckets: { label: string; n: number; total: number }[];
  topCoins: Holder[]; topPetals: Holder[];
}
export interface EconomySnapshot {
  from: string; to: string; latest: EconomySnapshotRow | null;
  trend: { day: string; saves: number; petals: number; coins: number }[];
}
export type SuspiciousReason = "earned" | "spent" | "converted" | "heavy_day";
export interface SuspiciousAccount {
  playerId: string; name?: string | null; earned: number; spent: number; converted: number; coins: number; bestDay: number;
  xMedian: number | null; reasons: SuspiciousReason[];
}
export interface SuspiciousActivity {
  from: string; to: string; accounts: number;
  thresholds: Record<"earned" | "spent" | "converted" | "bestDay", { p999: number; p50: number }>;
  rules: { pct: number; xMedian: number; minPetals: number; heavyDay: number };
  flagged: SuspiciousAccount[];
  holders: (Holder & { reason: "coins" | "petals" })[];
  snapshotDay: string | null;
}
export interface EconomyPage {
  "economy-report": EconomyReport; "economy-funnels": EconomyFunnels; "economy-snapshot": EconomySnapshot; "economy-suspicious": SuspiciousActivity;
}

export async function adminEconomy<K extends keyof EconomyPage>(kind: K, from: string, to: string): Promise<EconomyPage[K]> {
  const res = await fetch(`/api/admin/analytics/${kind}?${new URLSearchParams({ from, to })}`, { headers: await authHeaders() });
  return (await asJson(res)) as EconomyPage[K];
}

/**
 * THE ENGAGEMENT PAGE (server/analyticsEngagement.js). The report's series run over the comparison period + the range
 * (`days`, like AnalyticsSeries): the last n are the range. Cohorts and the level snapshot are written once a UTC day by
 * the server's job and are not tied to the range.
 */
export interface EngagementReport {
  from: string; to: string; prevFrom: string; prevTo: string; days: string[];
  series: Record<"dau" | "wau" | "mau" | "new" | "returning" | "active", number[]>;
  /** per play_counts counter (harvests, fishCaught, msGarden, …): its sum per day, and how many players used it */
  play: Record<string, number[]>;
  reach: Record<string, number[]>;
}
export interface Band { label: string; n: number }
export interface EngagementSessions {
  from: string; to: string; sessions: number; timed: number;
  length: { median: number; p90: number; mean: number }; lengths: Band[];
  players: number; playerSessions: number; perPlayer: { mean: number; median: number }; perPlayerBands: Band[];
}
export interface CohortRate { returned: number; eligible: number }
export interface Cohort {
  week: string; size: number; updatedAt: string; d1: CohortRate; d7: CohortRate; d30: CohortRate;
  weeks: { returned: number; matured: number }[];
}
export interface EngagementCohorts { weeks: number; cohorts: Cohort[]; overall: { d1: CohortRate; d7: CohortRate; d30: CohortRate } }
export type LevelTrack = "garden" | "fish" | "bug";
export interface EngagementSnapshotRow {
  day: string; takenAt: string; saves: number; cap: number; capOn: boolean; maxLevel: number;
  levels: Record<LevelTrack, { level: number; n: number }[]>;
  atCap: Record<LevelTrack | "any", number>;
  usage: { players: number; greenhouseMs: number; gardenMs: number; fishingMs: number; sessions: number };
}
export interface EngagementPage {
  "engagement-report": EngagementReport; "engagement-sessions": EngagementSessions;
  "engagement-cohorts": EngagementCohorts; "engagement-snapshot": { latest: EngagementSnapshotRow | null };
}

export async function adminEngagement<K extends keyof EngagementPage>(kind: K, from: string, to: string): Promise<EngagementPage[K]> {
  const res = await fetch(`/api/admin/analytics/${kind}?${new URLSearchParams({ from, to })}`, { headers: await authHeaders() });
  return (await asJson(res)) as EngagementPage[K];
}

/**
 * THE TECH HEALTH PAGE (server/analyticsHealth.js). The server half's series are per time BUCKET (`times`, ISO starts,
 * `stepSec` wide), clipped to the 30 days the minute table keeps; its daily maps run over the range's `days`. The
 * client half is the players' error and performance reports (public traffic). Latencies are bucket upper bounds (ms).
 */
export type HealthServerSeries =
  | "requests" | "e4" | "e5" | "avgMs" | "p50" | "p95" | "p99" | "rssMb" | "heapMb" | "loopP50" | "loopP99" | "loopMax"
  | "pgWaiting" | "pgTotal" | "queued" | "dropped" | "accepted";
export interface HealthRoute {
  route: string; n: number; e4: number; e5: number; errRate: number; avgMs: number; p50: number; p95: number; p99: number; maxMs: number;
}
export interface HealthServerSummary {
  minutes: number; instances: number; requests: number; e5: number; e4: number; errRate: number; p50: number; p95: number; p99: number;
  loopP99: number; loopMax: number; rssPeakMb: number; heapPeakMb: number; pgWaitingPeak: number; pgMax: number;
  queuedPeak: number; maxRows: number; dropped: number; rateLimited: number; conflicts: number; clamps: number;
}
export interface HealthServer {
  from: string; to: string; retainDays: number; retained: boolean; clipped: boolean; lo: string | null; hi: string | null;
  stepSec: number; times: string[]; series: Partial<Record<HealthServerSeries, number[]>>; routes: HealthRoute[];
  summary: HealthServerSummary;
  days: string[]; daily: { conflicts: number[]; clamps: Record<string, number[]>; rate: Record<string, number[]> };
}
export interface ClientErrorGroup {
  msg: string; frame: string; kind: string; n: number; sessions: number; firstSeen: string | null; lastSeen: string | null;
  build: string | null; builds: number; hasStack: boolean; browsers: { browser: string; n: number }[];
}
export interface ClientPerfRow {
  loads: number; cold: number; coldP50: number; coldP90: number; warmP50: number; cpuMs: number; cpuLoads: number; lowGfx: number;
}
export interface HealthClient {
  from: string; to: string; days: string[];
  daily: { errors: number[]; sessions: number[]; webgl: number[] };
  totals: { errors: number; sessions: number; groups: number; webgl: number; webglSessions: number };
  groups: ClientErrorGroup[];
  perf: {
    overall: ClientPerfRow; byDevice: (ClientPerfRow & { device: string })[]; byBrowser: (ClientPerfRow & { browser: string })[];
    players: number; lowGfxPlayers: number;
  };
}
export interface ClientErrorSample {
  ts: string | null; page: string | null; kind: string | null; build: string | null; stack: string | null;
  browser: string | null; os: string | null; device: string | null;
}
export interface HealthPage { "health-server": HealthServer; "health-client": HealthClient }

export async function adminHealth<K extends keyof HealthPage>(kind: K, from: string, to: string): Promise<HealthPage[K]> {
  const res = await fetch(`/api/admin/analytics/${kind}?${new URLSearchParams({ from, to })}`, { headers: await authHeaders() });
  return (await asJson(res)) as HealthPage[K];
}
/** One error group's samples (message + top frame), those with a stack first. */
export async function adminClientErrorSamples(from: string, to: string, msg: string, frame: string): Promise<{ msg: string; frame: string; samples: ClientErrorSample[] }> {
  const res = await fetch(`/api/admin/analytics/client-error?${new URLSearchParams({ from, to, msg, frame })}`, { headers: await authHeaders() });
  return (await asJson(res)) as { msg: string; frame: string; samples: ClientErrorSample[] };
}

export async function adminVisitorSummary<K extends keyof VisitorSummaries>(kind: K, from: string, to: string): Promise<VisitorSummaries[K]> {
  const res = await fetch(`/api/admin/analytics/${kind}?${new URLSearchParams({ from, to })}`, { headers: await authHeaders() });
  return (await asJson(res)) as VisitorSummaries[K];
}

// ---- SAVE BACKUPS (server/backups.js; Admin → Players → Backups and the player page's 🛟 Backups tab) ----------

export interface AdminBackupKind { label: string; icon: string; keepDays: number | null; auto: boolean }
/** One backup as the pages list it — never the save itself. */
export interface AdminBackup {
  id: number; playerId: string; playerName: string | null; kind: string; kindLabel: string; icon: string; label: string;
  createdAt: string; createdBy: string; keep: boolean; bytes: number; keptUntil: string | null;
  coins: number; petals: number; achievements: number; plantings: number; decor: number;
  levels: { garden: number; fish: number; bug: number };
}
export interface AdminBackupRun { at: number; count: number; label?: string; error?: string; ms?: number; pruned?: number }
export interface AdminBackupStatus {
  env: "production" | "staging"; ready: boolean;
  status: { daily?: AdminBackupRun; update?: AdminBackupRun; everyone?: AdminBackupRun; lastBuild?: string };
  totals: { backups: number; players: number; bytes: number }; savers: number; uncovered: number;
  recent: AdminBackup[]; kinds: Record<string, AdminBackupKind>;
}
export interface AdminRestorePreview {
  backup: AdminBackup; rows: Array<{ label: string; now: number; then: number; changed: boolean }>;
  carried: string[]; online: boolean; hasCurrent: boolean;
}
const bjson = async (res: Response) => asJson(res);
export async function adminBackupStatus(): Promise<AdminBackupStatus> {
  return (await bjson(await fetch("/api/admin/backups", { headers: await authHeaders() }))) as AdminBackupStatus;
}
export async function adminBackupEveryone(): Promise<{ ok: true; count: number }> {
  return (await bjson(await fetch("/api/admin/backups/run", { method: "POST", headers: await authHeaders() }))) as { ok: true; count: number };
}
export async function adminPlayerBackups(playerId: string): Promise<{ env: "production" | "staging"; backups: AdminBackup[] }> {
  return (await bjson(await fetch(`/api/admin/players/${encodeURIComponent(playerId)}/backups`, { headers: await authHeaders() }))) as { env: "production" | "staging"; backups: AdminBackup[] };
}
export async function adminMakeBackup(playerId: string, label: string): Promise<{ ok: true; id: number }> {
  return (await bjson(await fetch(`/api/admin/players/${encodeURIComponent(playerId)}/backups`, {
    method: "POST", headers: { ...(await authHeaders()), "Content-Type": "application/json" }, body: JSON.stringify({ label }),
  }))) as { ok: true; id: number };
}
export async function adminRestorePreview(id: number): Promise<AdminRestorePreview> {
  return (await bjson(await fetch(`/api/admin/backups/${id}/preview`, { headers: await authHeaders() }))) as AdminRestorePreview;
}
export async function adminRestoreBackup(id: number): Promise<{ ok: true; safetyBackupId: number | null; live: "reloaded" | "offline" }> {
  return (await bjson(await fetch(`/api/admin/backups/${id}/restore`, { method: "POST", headers: await authHeaders() }))) as
    { ok: true; safetyBackupId: number | null; live: "reloaded" | "offline" };
}
export async function adminEditBackup(id: number, patch: { keep?: boolean; label?: string }): Promise<void> {
  await bjson(await fetch(`/api/admin/backups/${id}`, {
    method: "PATCH", headers: { ...(await authHeaders()), "Content-Type": "application/json" }, body: JSON.stringify(patch),
  }));
}
export async function adminDeleteBackup(id: number): Promise<void> {
  await bjson(await fetch(`/api/admin/backups/${id}`, { method: "DELETE", headers: await authHeaders() }));
}
/** Download a backup as a .json file (fetched with the admin's sign-in, then saved by the browser). */
export async function adminDownloadBackup(id: number): Promise<void> {
  const res = await fetch(`/api/admin/backups/${id}/download`, { headers: await authHeaders() });
  if (!res.ok) await bjson(res);
  const name = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") || "")?.[1] || `backup-${id}.json`;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
export async function adminLoadBackupIntoMine(id: number): Promise<{ ok: true; safetyBackupId: number | null }> {
  return (await bjson(await fetch(`/api/admin/backups/${id}/load-into-mine`, { method: "POST", headers: await authHeaders() }))) as
    { ok: true; safetyBackupId: number | null };
}
/** (Test server) copy backup `id` from the LIVE server here; `load` also puts it into your own test account. */
export async function adminImportBackupFromLive(id: number, load: boolean): Promise<{ ok: true; backupId: number; playerId: string }> {
  return (await bjson(await fetch("/api/admin/backups/import-from-live", {
    method: "POST", headers: { ...(await authHeaders()), "Content-Type": "application/json" }, body: JSON.stringify({ id, load }),
  }))) as { ok: true; backupId: number; playerId: string };
}
